import { useState } from 'react';
import { buildTearsheetPath } from '@/lib/tearsheet/openTearsheet';
import {
  EMPTY_TEARSHEET_LOCATION,
  geoFromLocation,
} from '@/components/pe/tearsheet/tearsheetLocation';
import { TearsheetLocationFields } from '@/components/pe/tearsheet/TearsheetLocationFields';
import {
  TearsheetGammaNotice,
  useGammaTearsheetGate,
} from '@/components/pe/tearsheet/TearsheetGenerateLink';
import type { LocationGroup } from '@/types';

export default function ResearchHomeTearsheet() {
  const [tsName, setTsName] = useState('');
  const [tsWebsite, setTsWebsite] = useState('');
  const [tsLocation, setTsLocation] = useState<LocationGroup[]>(EMPTY_TEARSHEET_LOCATION);
  const [tsLocationError, setTsLocationError] = useState('');
  const gammaG = useGammaTearsheetGate();

  const openTs = () => {
    if (gammaG.disabled) return;
    const name = tsName.trim();
    if (!name) return;
    const geo = geoFromLocation(tsLocation);
    if (!geo.country) {
      setTsLocationError('Select a country so we can tell similarly named companies apart.');
      return;
    }
    setTsLocationError('');
    window.open(
      buildTearsheetPath(name, tsWebsite.trim() || undefined, undefined, geo),
      '_blank',
      'noopener,noreferrer',
    );
  };

  return (
    <section className="rh-panel rh-tearsheet" aria-labelledby="rh-ts-heading">
      <div className="rh-panel-head">
        <div className="rh-panel-title">
          <i className="bi bi-file-earmark-text" aria-hidden="true" />
          <h2 id="rh-ts-heading">Quick tool: Company tearsheet</h2>
        </div>
        <p className="rh-tearsheet-hint">AI-generated one-pager — opens in a new tab</p>
      </div>
      <div className="rh-panel-body rh-tearsheet-form">
        <div className="rh-ts-field">
          <label htmlFor="rh-ts-name">Company name</label>
          <input
            id="rh-ts-name"
            type="text"
            value={tsName}
            onChange={(e) => setTsName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') openTs();
            }}
            placeholder="e.g. Acme Corporation"
          />
        </div>
        <div className="rh-ts-field">
          <label htmlFor="rh-ts-web">
            Website <span className="rh-opt">(optional)</span>
          </label>
          <input
            id="rh-ts-web"
            type="url"
            value={tsWebsite}
            onChange={(e) => setTsWebsite(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') openTs();
            }}
            placeholder="e.g. https://acmecorp.com"
          />
        </div>
        <div className="rh-ts-field rh-ts-location">
          <TearsheetLocationFields
            value={tsLocation}
            onChange={(rows) => {
              setTsLocation(rows);
              if (tsLocationError) setTsLocationError('');
            }}
            error={tsLocationError}
          />
        </div>
        <TearsheetGammaNotice />
        <button
          type="button"
          className="rh-build-btn rh-ts-btn"
          disabled={gammaG.disabled || !tsName.trim()}
          title={gammaG.disabled ? gammaG.reason : undefined}
          onClick={openTs}
        >
          <i className="bi bi-file-earmark-text" aria-hidden="true" />
          Generate tearsheet
        </button>
      </div>
    </section>
  );
}
